import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, RotateCcw } from 'lucide-react';
import { apiReports } from '../api/client.js';
import { hoursToDays } from '../lib/units.js';
import DatePicker from './DatePicker.jsx';

/**
 * Ticket #113 (lot 2) — Relevé d'heures (section de l'Accueil, tous rôles).
 *
 * Besoin : remplir le CERFA 2041-AE (« nombre total d'heures de travail
 * accomplies par le salarié dans l'année »). Ventilation dépliable :
 *   1. Affaires facturables → détail par affaire
 *   2. Projets internes / non facturables → détail par affaire
 *   3. Congés et absences (approuvés) → détail par type
 * puis Total travaillé, Congés, Total. Heures et/ou jours (1 j = 8 h).
 *
 * Périmètre borné serveur (`listVisiblePersonIds`) : un collaborateur ne voit
 * que son relevé (pas de sélecteur) ; un manager le sien et celui de son équipe
 * directe ; un administrateur celui de tous.
 *
 * Réglages (période, unité, personne, catégories dépliées) mémorisés en
 * localStorage par utilisateur — préférence de poste, jamais indispensable.
 */

const PRESETS = [
  { key: 'thisYear', label: 'Année en cours' },
  { key: 'lastYear', label: 'Année précédente' },
  { key: 'thisMonth', label: 'Ce mois' },
  { key: 'lastMonth', label: 'Mois dernier' },
  { key: 'custom', label: 'Personnalisée' },
];
const UNITS = [
  { key: 'both', label: 'h + j' },
  { key: 'h', label: 'Heures' },
  { key: 'd', label: 'Jours' },
];
const DEFAULT_PRESET = 'thisYear';
const DEFAULT_UNIT = 'both';
const MAX_DAYS = 366;
const MS_PER_DAY = 86_400_000;

const iso = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
const frDate = (s) => (s ? s.split('-').reverse().join('/') : '');

function presetWindow(key, now = new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (key) {
    case 'lastYear':
      return { from: iso(utc(y - 1, 0, 1)), to: iso(utc(y - 1, 11, 31)) };
    case 'thisMonth':
      return { from: iso(utc(y, m, 1)), to: iso(utc(y, m + 1, 0)) };
    case 'lastMonth':
      return { from: iso(utc(y, m - 1, 1)), to: iso(utc(y, m, 0)) };
    case 'thisYear':
    default:
      return { from: iso(utc(y, 0, 1)), to: iso(utc(y, 11, 31)) };
  }
}

const storageKey = (userId) => `synolog.home.timeStatement.${userId ?? 'anon'}`;

function loadPrefs(userId) {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
}

function savePrefs(userId, prefs) {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    /* stockage indisponible : préférence non mémorisée, sans impact */
  }
}

const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

function TimeValue({ hours, unit, strong = false }) {
  const h = `${nf.format(hours)} h`;
  const d = `${nf.format(hoursToDays(hours))} j`;
  const main = { color: 'var(--text)', fontWeight: strong ? 600 : 500, whiteSpace: 'nowrap' };
  if (unit === 'h') return <span style={main}>{h}</span>;
  if (unit === 'd') return <span style={main}>{d}</span>;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
      <span style={main}>{h}</span>
      <span style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{d}</span>
    </span>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-pill)',
        padding: '5px 12px',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

const selectStyle = {
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 8px',
  fontSize: 12,
  maxWidth: '100%',
};
const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 4px',
  borderBottom: '1px solid var(--glass-border)',
  minWidth: 0,
};
const label = { fontSize: 12, color: 'var(--text-muted)', minWidth: 60 };

const EMPTY_HINT = {
  billable: 'Aucun temps saisi sur une affaire facturable.',
  internal: 'Aucun temps saisi sur un projet interne ou non facturable.',
  timeoff: 'Aucun congé approuvé sur la période.',
};

function ItemLine({ item, unit, isTimeOff }) {
  return (
    <li style={{ ...rowStyle, padding: '6px 4px 6px 28px' }}>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ color: 'var(--text)', fontSize: 13, overflowWrap: 'anywhere' }}>{item.name}</span>
        {!isTimeOff && (item.code || item.clientName) ? (
          <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12, overflowWrap: 'anywhere' }}>
            {[item.code, item.clientName].filter(Boolean).join(' · ')}
          </span>
        ) : null}
        {!isTimeOff && item.nonBillableHours > 0 && item.billableHours > 0 ? (
          <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11 }}>
            dont {nf.format(item.nonBillableHours)} h saisies non facturables
          </span>
        ) : null}
      </span>
      <TimeValue hours={item.hours} unit={unit} />
    </li>
  );
}

export default function TimeStatementSection({ user, sectionTitle }) {
  const userId = user?.id;
  const initial = useMemo(() => loadPrefs(userId), [userId]);
  const [preset, setPreset] = useState(
    PRESETS.some((p) => p.key === initial?.preset) ? initial.preset : DEFAULT_PRESET,
  );
  const [custom, setCustom] = useState(
    initial?.custom?.from && initial?.custom?.to ? initial.custom : presetWindow(DEFAULT_PRESET),
  );
  const [unit, setUnit] = useState(UNITS.some((u) => u.key === initial?.unit) ? initial.unit : DEFAULT_UNIT);
  // `null` = ma propre fiche (défaut serveur).
  const [personId, setPersonId] = useState(
    typeof initial?.personId === 'string' && initial.personId ? initial.personId : null,
  );
  const [expanded, setExpanded] = useState(() =>
    Array.isArray(initial?.expanded) ? initial.expanded.filter((k) => typeof k === 'string') : [],
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const win = preset === 'custom' ? custom : presetWindow(preset);
  const spanDays =
    win.from && win.to ? Math.round((Date.parse(win.to) - Date.parse(win.from)) / MS_PER_DAY) + 1 : 0;
  const invalidRange = !win.from || !win.to || win.from > win.to;
  const tooWide = !invalidRange && spanDays > MAX_DAYS;

  useEffect(() => {
    savePrefs(userId, { preset, custom, unit, personId, expanded });
  }, [userId, preset, custom, unit, personId, expanded]);

  useEffect(() => {
    if (invalidRange || tooWide) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiReports
      .timeStatement({ from: win.from, to: win.to, personId })
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (cancelled) return;
        // Personne mémorisée sortie du périmètre : retour à ma propre fiche.
        if (e.status === 404 && personId) {
          setPersonId(null);
          return;
        }
        setData(null);
        setError(e.status === 404 ? e.message || 'Aucune fiche personne liée à votre compte' : e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [win.from, win.to, personId, invalidRange, tooWide]);

  const persons = data?.persons ?? [];
  const showPersonPicker = persons.length > 1;
  const selectedPersonId = personId ?? data?.person?.id ?? '';

  const toggle = (key) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const resetAll = () => {
    setPreset(DEFAULT_PRESET);
    setCustom(presetWindow(DEFAULT_PRESET));
    setUnit(DEFAULT_UNIT);
    setPersonId(null);
    setExpanded([]);
  };
  const isDefault = preset === DEFAULT_PRESET && unit === DEFAULT_UNIT && personId === null;

  const isEmpty = data && data.totalHours === 0;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 style={sectionTitle}>
        <ClipboardList size={16} /> Relevé d'heures
      </h2>

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          minWidth: 0,
        }}
      >
        {/* ---- Période ---- */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={label}>Période</span>
          {PRESETS.map((p) => (
            <Pill key={p.key} active={preset === p.key} onClick={() => setPreset(p.key)}>
              {p.label}
            </Pill>
          ))}
          {preset === 'custom' ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <DatePicker
                value={custom.from}
                required
                max={custom.to || undefined}
                ariaLabel="Date de début du relevé"
                onChange={(v) => setCustom((c) => ({ ...c, from: v }))}
              />
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
              <DatePicker
                value={custom.to}
                required
                min={custom.from || undefined}
                ariaLabel="Date de fin du relevé"
                onChange={(v) => setCustom((c) => ({ ...c, to: v }))}
              />
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {frDate(win.from)} → {frDate(win.to)}
            </span>
          )}
        </div>

        {/* ---- Personne + unité ---- */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {showPersonPicker ? (
            <>
              <span style={label}>Personne</span>
              <select
                aria-label="Relevé de la personne"
                value={selectedPersonId}
                onChange={(e) => setPersonId(e.target.value && e.target.value !== data?.myPersonId ? e.target.value : null)}
                disabled={loading}
                style={selectStyle}
              >
                {persons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.id === data?.myPersonId ? ' (moi)' : ''}
                    {p.archived ? ' (parti·e)' : ''}
                  </option>
                ))}
              </select>
            </>
          ) : data?.person ? (
            <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{data.person.name}</span>
          ) : null}
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {UNITS.map((u) => (
              <Pill key={u.key} active={unit === u.key} onClick={() => setUnit(u.key)}>
                {u.label}
              </Pill>
            ))}
            <button
              type="button"
              onClick={resetAll}
              disabled={isDefault}
              title="Revenir aux réglages par défaut (année en cours, h + j, ma fiche)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-pill)',
                padding: '5px 12px',
                fontSize: 12,
                cursor: isDefault ? 'not-allowed' : 'pointer',
                opacity: isDefault ? 0.45 : 1,
              }}
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
          </span>
        </div>

        {/* ---- Relevé ---- */}
        {invalidRange ? (
          <div style={{ color: 'var(--warning)', fontSize: 13 }}>Choisissez une date de début et une date de fin valides.</div>
        ) : tooWide ? (
          <div style={{ color: 'var(--warning)', fontSize: 13 }}>
            La période ne peut pas dépasser {MAX_DAYS} jours ({spanDays} jours choisis).
          </div>
        ) : error ? (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>
        ) : loading && !data ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
        ) : !data ? null : (
          <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms', minWidth: 0 }}>
            {isEmpty ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, paddingBottom: 8 }}>
                Aucune heure saisie ni congé approuvé sur cette période.
              </div>
            ) : null}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {data.categories.map((c, i) => {
                const open = expanded.includes(c.key);
                const panelId = `time-statement-${c.key}`;
                return (
                  <li key={c.key}>
                    <button
                      type="button"
                      onClick={() => toggle(c.key)}
                      aria-expanded={open}
                      aria-controls={panelId}
                      style={{
                        ...rowStyle,
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--glass-border)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text)',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          {i + 1}. {c.label}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({c.items.length})</span>
                      </span>
                      <TimeValue hours={c.hours} unit={unit} strong />
                    </button>
                    {open ? (
                      <ul id={panelId} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {c.items.length === 0 ? (
                          <li style={{ ...rowStyle, padding: '6px 4px 6px 28px', color: 'var(--text-muted)', fontSize: 12 }}>
                            {EMPTY_HINT[c.key]}
                          </li>
                        ) : (
                          c.items.map((item) => (
                            <ItemLine
                              key={item.affaireId ?? item.timeOffTypeId ?? item.name}
                              item={item}
                              unit={unit}
                              isTimeOff={c.key === 'timeoff'}
                            />
                          ))
                        )}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
              {[
                { key: 'worked', text: 'Total travaillé', hours: data.workedHours },
                { key: 'off', text: 'Congés', hours: data.timeOffHours },
                { key: 'total', text: 'Total', hours: data.totalHours, strong: true },
              ].map((t) => (
                <li
                  key={t.key}
                  style={{
                    ...rowStyle,
                    borderBottom: t.strong ? 'none' : rowStyle.borderBottom,
                    borderTop: t.strong ? '2px solid var(--glass-border)' : undefined,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: t.strong ? 700 : 600, color: 'var(--text)', paddingLeft: 24 }}>
                    {t.text}
                  </span>
                  <TimeValue hours={t.hours} unit={unit} strong />
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Toutes affaires, internes comprises · le classement facturable / interne suit l’affaire · congés
          approuvés uniquement, hors jours fériés et non travaillés · 1 jour = 8 h.
        </div>
      </div>
    </section>
  );
}
